import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { groupBy, orderBy } from "lodash";
import moment from "moment";

import { useUser } from "@components/user-context";
import { KpiPeriodSelector } from "@features/mmm/components/model-insights/kpi-period-selector";
import { useExploreMixesTimePeriod } from "@features/mmm/hooks/mmm-url-state";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { type RenderTreeNodePayload, Tree, useTree } from "@mantine/core";
import {
  type FetchHistoricalMixForCycle,
  type FetchHistoricalMixForCycleVariables,
} from "@north-beam/nb-client-side";
import { formatCurrency } from "@north-beam/nb-common/formatting";
import { IconButton, PrimaryButton, SecondaryButton } from "@shared/buttons";
import { renderChevronAnimation } from "@shared/selects/utils";

import { HISTORICAL_MIXES_FOR_CYCLE_QUERY } from "../explore-budgets-v2/explore-budgets-queries";
import { useSelectedCycle } from "../hooks/use-selected-cycle";

const dateRangeMap = {
  last_26_weeks: {
    startDate: moment().subtract(26, "weeks").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  },
  last_52_weeks: {
    startDate: moment().subtract(52, "weeks").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  },
  last_year: {
    startDate: moment()
      .subtract(1, "year")
      .startOf("year")
      .format("YYYY-MM-DD"),
    endDate: moment().subtract(1, "year").endOf("year").format("YYYY-MM-DD"),
  },
  all_time: {
    startDate: "1970-01-01",
    endDate: moment().format("YYYY-MM-DD"),
  },
};

export const SpendByChannels = () => {
  const { hasFeatureFlag } = useFeatureFlags();
  const [dateRange, setDateRange] = useExploreMixesTimePeriod();
  const { selectedCycle } = useSelectedCycle();

  const [currentSort, setCurrentSort] = useState<"asc" | "desc">("desc");

  const tree = useTree();

  const { data: historicalMixData, loading: historicalMixDataLoading } =
    useNorthbeamQuery<
      FetchHistoricalMixForCycle,
      FetchHistoricalMixForCycleVariables
    >(HISTORICAL_MIXES_FOR_CYCLE_QUERY, {
      variables: {
        id: selectedCycle.id,
        dateRange: dateRangeMap[dateRange as keyof typeof dateRangeMap],
      },
    });

  useEffect(() => {
    if (!historicalMixDataLoading) {
      tree.expandAllNodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historicalMixDataLoading]);

  const channelsWithSpend = useMemo(() => {
    const data =
      historicalMixData?.me?.mmm?.cycle?.historicalMix?.data?.spend_data ?? [];

    const result: {
      keyForIcon: string;
      column_name: string;
      breakdowns: { key: string; value: string }[];
      label: string;
      spend: number;
      value: string;
      children: any[];
    }[] = [];

    const groupedBreakdowns = groupBy(
      data,
      (row) => row.breakdowns?.[0]?.value,
    );

    Object.entries(groupedBreakdowns).forEach(([breakdown, subBreakdowns]) => {
      const breakdownSum = subBreakdowns.reduce(
        (acc, row) => acc + row.spend,
        0,
      );

      const subBreakdownsWithChildren = subBreakdowns.filter(
        (row) => row.breakdowns.length > 1,
      );
      const children = orderBy(
        subBreakdownsWithChildren,
        "spend",
        currentSort,
      ).map((row) => ({
        column_name: row.data_column_name,
        breakdowns: row.breakdowns,
        label: row.breakdowns[1].value,
        value: row.data_column_name,
        spend: row.spend,
      }));

      result.push({
        keyForIcon: breakdown,
        column_name: breakdown,
        breakdowns: [],
        label: breakdown,
        spend: breakdownSum,
        value: breakdown,
        children,
      });
    });

    return orderBy(result, "spend", currentSort);
  }, [historicalMixData, currentSort]);

  if (!hasFeatureFlag("enableMmmPerformanceAndIncrementalUiUpdates")) {
    return null;
  }

  const hasAllExpanded = Object.entries(tree.expandedState).every(
    ([, value]) => value,
  );
  const hasAnyExpanded = Object.entries(tree.expandedState).some(
    ([, value]) => value,
  );
  const isExpandable = channelsWithSpend.some(
    (channel) => channel.children.length > 0,
  );

  return (
    <div className="bg-v2-primary border border-v2-secondary rounded-lg px-6 py-5 flex-grow h-inherit">
      <div className="flex items-center justify-between">
        <KpiPeriodSelector
          className="mb-6"
          value={dateRange}
          onChange={setDateRange}
        />
        <div className="flex items-center space-x-1">
          {isExpandable && (
            <div className="whitespace-nowrap">
              <PrimaryButton
                className="rounded-r-none border border-r-0"
                isDisabled={hasAllExpanded}
                onClick={tree.expandAllNodes}
              >
                EXPAND
              </PrimaryButton>
              <SecondaryButton
                className="rounded-l-none"
                isDisabled={!hasAnyExpanded}
                onClick={tree.collapseAllNodes}
              >
                COLLAPSE
              </SecondaryButton>
            </div>
          )}
          <IconButton
            className="h-[34px]"
            variant="secondary"
            size="md"
            onClick={() =>
              setCurrentSort(currentSort === "asc" ? "desc" : "asc")
            }
          >
            <div className="flex items-center 3xl:space-x-1.5 leading-0">
              <span className="3xl:block hidden">SORT BY SPEND</span>
              <i
                className={clsx("fa-regular fa-arrow-down", {
                  "transform rotate-180": currentSort === "asc",
                })}
              />
            </div>
          </IconButton>
        </div>
      </div>
      {historicalMixDataLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded-md bg-gray-2 h-12 w-full"
            ></div>
          ))}
        </div>
      ) : (
        <Tree
          className="overflow-y-auto h-[90%]"
          data={channelsWithSpend as any}
          tree={tree}
          renderNode={(payload) => <ChannelInfo {...payload} />}
        />
      )}
    </div>
  );
};

const ChannelInfo = ({
  node,
  level,
  expanded,
  hasChildren,
  elementProps,
}: RenderTreeNodePayload & { node: any }) => {
  const { user } = useUser();
  const { designElementManager } = useDesignElementManager();

  return (
    <div
      {...elementProps}
      className={clsx("flex items-center justify-between space-x-4 px-4 py-3", {
        "cursor-pointer": hasChildren,
      })}
      style={{ marginLeft: `${(level - 1) * 42}px` }}
    >
      <div className="flex items-center space-x-3 font-semibold">
        {hasChildren && renderChevronAnimation(expanded)}
        {node.keyForIcon &&
          designElementManager
            .getDesignElement(node.keyForIcon)
            .getComponent({ size: "20px" })}
        <span>{node.label}</span>
      </div>
      <span className="font-mono">
        {formatCurrency(node.spend, { code: user.currencies.currentCurrency })}
      </span>
    </div>
  );
};
